"use client";
import { defaultLocale, deleteInquiryByProductId, getInquiry, productTranslationName, handleGraphqlLocale } from "@/lib/utils/util";
import { Link } from "@/navigation";
import React, { useEffect, useState } from "react";
import EmptyState from "../EmptyState";
import { useLocale, useTranslations } from "next-intl";
import ContactForm from "../InquryForm/ContactForm";
import { getImagesForProduct } from "../Product/imageGallery";
import CommonTaile from "../Common/CommonTaile";
import { BodyText } from "../BodyText";
import { getChannelLanguageMap } from "@/lib/api/channel";
import { executeGraphQL } from "@/lib/utils/graphql";
import { ProductListByIdsDocument } from "@/gql/graphql";
import { App } from "antd";

 function InquiryData() {
	const [InquiryList, setInquiryList] = useState(getInquiry());
	const [productsData, setProductsData] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState(false);
	const t = useTranslations();
	const { message } = App.useApp();

	let locale = useLocale();

    function remove(id: string) {
        const deleted = deleteInquiryByProductId(id);
        // 更新列表
        setInquiryList(getInquiry());
        if (deleted) {
        	message.success(t("message.inquiryDeletedSuccess"));
        } else {
        	message.error(t("message.inquiryItemNotFound"));
        }
    }

	// 根据当前语言动态获取产品数据
	useEffect(() => {
		const fetchProductsData = async () => {
			if (InquiryList.length === 0) return;
			
			setLoading(true);
			try {
				const productIds = InquiryList
					.map(item => item?.variant?.product?.id)
					.filter(Boolean) as string[];
				
				if (productIds.length === 0) {
					setLoading(false);
					return;
				}

				const channel = (await getChannelLanguageMap())[defaultLocale];
				// 使用更大的 first 值确保获取所有产品
				const products = await executeGraphQL(ProductListByIdsDocument, {
					withAuth: false,
					variables: {
						first: 100, // 增加数量以确保获取所有产品
						locale: handleGraphqlLocale(locale || defaultLocale),
						ids: productIds,
						channel: channel,
					},
					revalidate: 60,
				});

				// 将产品数据转换为以 ID 为 key 的对象
				const productsMap: Record<string, any> = {};
				products?.products?.edges?.forEach((edge: any) => {
					const product = edge.node;
					if (product?.id) {
						productsMap[product.id] = product;
					}
				});

				setProductsData(productsMap);
			} catch (error) {
				console.error('获取产品数据失败:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchProductsData();
	}, [InquiryList, locale]);

	useEffect(() => {
		const handleResize = () => {
			let Inquirydata = getInquiry();
			setInquiryList(Inquirydata);
		};
		handleResize();
		window.addEventListener("storage", handleResize);
	
		// Cleanup function to remove event listener
		return () => window.removeEventListener("storage", handleResize);
	}, []);

	const breadcrumbItems = [
		{ label: t("nav.Home"), link: "/" },
		{ label: t("nav.InquiryProducts"), link: ""},
	];

	return (
		<div>
			<CommonTaile 
				msg={t("nav.InquiryProducts")}
				backgroundImage="/image/plsyy/product-banner.webp"
				breadcrumbItems={breadcrumbItems}
				pageSlug="inquiry"
			/>
			{InquiryList.length ? (
				<div className="container my-12 sm:my-16 md:my-20">
					{/* 询盘列表 */}
					<div className="mb-8 sm:mb-12">
						<div className="grid gap-4 sm:gap-6">
							{InquiryList.map((item, index) => {
								return (
									<div 
										key={index} 
										className="group relative bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
									>
										<div className="flex flex-col sm:flex-row p-4 sm:p-6">
											{/* 产品图片 */}
											<Link 
												href={`/product/${item.variant?.product?.slug || item.slug || ''}`}
												className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-6"
											>
												<div className="relative w-full sm:w-32 md:w-40 h-48 sm:h-32 md:h-40 rounded-lg overflow-hidden bg-gray-100">
													<img
														src={getImagesForProduct(item.variant.product)[0]?.url || "/image/default-image2.webp"}
														alt={item.name}
														className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
													/>
												</div>
											</Link>
											
											{/* 产品信息 */}
											<div className="flex-1 flex flex-col justify-between min-w-0">
												<div className="flex-1">
													{/* 产品名称 - 从动态获取的产品数据中读取多语言名称 */}
													<Link
														href={`/product/${productsData[item.variant?.product?.id]?.slug || item.variant?.product?.slug || item.slug || ''}`}
														className="block mb-3"
													>
														<h3 className="text-lg sm:text-xl font-semibold text-gray-900 group-hover:text-mainColor transition-colors duration-200 line-clamp-2">
															{(() => {
																// 优先使用动态获取的产品数据（包含当前语言的翻译）
																const product = productsData[item.variant?.product?.id];
																if (product) {
																	// 与 Information 组件逻辑一致
																	return productTranslationName(product?.translation?.name) || 
																		   productTranslationName(product?.name) || 
																		   item.name;
																}
																// 如果没有动态数据，使用存储的数据
																return productTranslationName(item?.variant?.product?.translation?.name) || 
																	   productTranslationName(item?.variant?.product?.name) || 
																	   item.name;
															})()}
														</h3>
													</Link>
													
													{/* 产品详情 */}
													<div className="space-y-2">
														{/* 产品简单描述 - 从动态获取的产品数据中读取多语言描述 */}
														{(() => {
															// 优先使用动态获取的产品数据（包含当前语言的翻译）
															const product = productsData[item.variant?.product?.id];
															
															let description: any = null;
															let descriptionJson: any = null;
															
															if (product) {
																// 使用动态获取的数据，与 Information 组件逻辑一致
																description = product?.description ? JSON.parse(product.description) : null;
																descriptionJson = product?.translation?.descriptionJson
																	? JSON.parse(product.translation.descriptionJson)
																	: null;
															} else {
																// 如果没有动态数据，使用存储的数据
																description = item?.variant?.product?.descriptionJson 
																	? (typeof item.variant.product.descriptionJson === 'string' 
																		? JSON.parse(item.variant.product.descriptionJson) 
																		: item.variant.product.descriptionJson)
																	: null;
																
																descriptionJson = item?.variant?.product?.translation?.descriptionJson
																	? (typeof item.variant.product.translation.descriptionJson === 'string'
																		? JSON.parse(item.variant.product.translation.descriptionJson)
																		: item.variant.product.translation.descriptionJson)
																	: null;
															}
															
															// 获取简单描述文本（blocks[1]），优先使用翻译版本
															const shortDescription = descriptionJson?.blocks[1]?.data?.text || description?.blocks[1]?.data?.text;
															
															return shortDescription ? (
																<>
																	<div className="mt-3 w-full border border-gray-200" />
																	<BodyText
																		innerHTML={shortDescription}
																		className="text-sm py-2 sm:text-base font-normal text-gray-600"
																	/>
																	<div className="mb-3 w-full border border-gray-200" />
																</>
															) : null;
														})()}
														<div className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
															<span className="text-gray-500 font-medium">{t("product.Quantity")}:</span>
															<span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-mainColor/10 text-mainColor font-semibold">
																{item.quantity || 1}
															</span>
														</div>
													</div>
												</div>
											</div>
											
											{/* 删除按钮 */}
											<div className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 sm:ml-4">
												<button
													type="button"
													onClick={() => remove(item.variant.product.id)}
													className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200"
													aria-label={t("product.Remove")}
												>
													<svg
														className="w-5 h-5"
														viewBox="0 0 20 20"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"></path>
													</svg>
												</button>
											</div>
										</div>
									</div>
								);
							})}
						</div>
					</div>
					
					{/* 联系表单 */}
					<div className="bg-mainColor rounded-lg p-6 sm:p-8 md:p-10">
						{/* 询盘提示区域 */}
						<div className="mb-6 sm:mb-8">
							<div className="flex items-start gap-4 text-white">
								{/* 图标 */}
								<div className="flex-shrink-0 mt-1">
									<svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
									</svg>
								</div>
								<div className="flex-1">
									<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
										{t("base.getInstantQuoteAndTS")}
									</h2>
									<p className="text-base sm:text-lg leading-relaxed mb-2 opacity-90">
										{t("form.fillOutIn")}
									</p>
									{InquiryList.length > 0 && (
										<p className="text-sm sm:text-base font-medium opacity-80">
											{t("nav.InquiryProducts")}: <span className="font-bold">{InquiryList.length}</span> {InquiryList.length === 1 ? t("nav.item") : t("nav.items")}
										</p>
									)}
								</div>
							</div>
						</div>
						
						<ContactForm
							locale={locale}
							title={t("base.getInstantQuoteAndTS")}
							inquiry={InquiryList}
							source="inquiry"
							// @ts-ignore
							titleContent={t("form.fillOutIn")}
							className="!py-0"
							innerClassName="!shadow-none !rounded-lg bg-white p-10"
						/>
					</div>
				</div>
			) : (
				<EmptyState />
			)}
		</div>
	);
}


export default InquiryData;